import { Avatar, AvatarSize, Box, Icon, IconSize, Menu, MenuOptions, Popover, useSideSheetState } from '@bechtel/gds';
import { concat, map, toLower } from 'lodash';
import React, { FC, Fragment, lazy, Suspense, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink, useLocation } from 'react-router-dom';
import { ThemeName } from '~/app-theme';
import {
  AvatarPlaceholder,
  EmulateAction,
  ErrorBoundary,
  MultiLingualText,
  SuspenseLoader,
  useAppThemeContext,
  useAppUser,
} from '~/components';
import { envConfig } from '~/env-config';
import { useLocalStorage, useOutsideClick } from '~/hooks';
//@ts-ignore images ignore
import { ReactComponent as SetRouteLogo } from '~/images/setroute-logo-full.svg';
import ProjectSelector from './project-details';
import {
  AccountItemStyled,
  AccountSectionStyled,
  AppHeaderRightContainerStyled,
  AppHeaderStyled,
  Divider,
  HeaderBackdropStyled,
  HelpIconStyled,
  LogoSectionStyled,
  ThemeColorBadge,
  ThemeItemStyled,
  ToolTipTextStyled,
  TopMenuSelectedItemStyled,
} from './styled';

const MyAccount = lazy(() => import('~/components/organisms/my-account'));

const topBarItems = [
  // { label: 'Dashboard', to: '' },
  { label: 'Commodities', to: 'commodities/cable' },
  { label: 'Jobsite Feedback', to: 'jobsite-feedback/cable' },
  { label: 'Reports', to: 'reports/general' },
  { label: 'Tools', to: 'tools/analyze' },
];

const adminTopBarItems = [{ label: 'Admin', to: 'admin/project' }];

const topBarAccountSectionItems = [
  { label: 'My Account', to: 'my-account', id: 'MY_ACCOUNT' },
  { label: 'Project Settings', to: 'project-settings', id: 'PROJECT_SETTINGS' },
  { label: 'Change Theme', to: 'change-theme', id: 'THEME' },
  { label: 'Emulate', to: 'emulate', id: 'EMULATE' },
];

const TopMenu = React.memo(() => {
  const { isSystemAdmin } = useAppUser();

  const topMenuItems = useMemo(() => {
    if (isSystemAdmin) {
      return concat(topBarItems, adminTopBarItems);
    }
    return topBarItems;
  }, [isSystemAdmin]);

  return (
    <>
      {map(topMenuItems, (item) => (
        <NavLink key={item.to} to={`/${item.to}`} style={{ textDecoration: 'none' }}>
          <TopMenuItem item={item} />
        </NavLink>
      ))}
    </>
  );
});

const TopMenuItem = ({ item }) => {
  const location = useLocation();
  // remove leading '/' in the location path eg input: /commodities/equipments output: commodities/equipments
  const path = location.pathname.slice(1, location.pathname.length);
  // get the top menu from the navigation url eg input: commodities/equipments output: commodities
  const topMenuFromPath = path.slice(0, path.indexOf('/') !== -1 ? path.indexOf('/') : path.length);
  // get the top menu from the item to url eg input: commodities/equipments output: commodities
  const topMenuFromTo = item.to.slice(0, item.to.indexOf('/') !== -1 ? item.to.indexOf('/') : item.to.length);
  const isActive = toLower(topMenuFromTo) === toLower(topMenuFromPath);
  return (
    <TopMenuSelectedItemStyled isActive={isActive}>
      <MultiLingualText color="primary.900" family="openSansCondensed" size="400" weight={isActive ? '700' : '500'}>
        {item.label}
      </MultiLingualText>
    </TopMenuSelectedItemStyled>
  );
};

const TopMenuAccountSection: FC<{ onMenuClick; toggle }> = React.memo(({ onMenuClick, toggle }) => {
  const ref = useRef();
  useOutsideClick(ref, () => {
    toggle();
  });
  const { currentTheme, setCurrentTheme } = useAppThemeContext();
  const [themeSettings, setThemeSettings] = useLocalStorage('THEME_SETTINGS');
  const allThemes = Object.keys(ThemeName);

  return (
    <AccountSectionStyled ref={ref}>
      {map(topBarAccountSectionItems, (item) => {
        return (
          <Fragment key={item.id}>
            {item.id === 'EMULATE' ? (
              <>
                {!!envConfig.enableEmulation && (
                  <AccountItemStyled>
                    <EmulateAction toggle={toggle} />
                  </AccountItemStyled>
                )}
              </>
            ) : item.id === 'THEME' ? (
              <Menu>
                {({ placement, isOpen, toggle, close, referenceRef, popperRef }) => (
                  <>
                    <AccountItemStyled onClick={toggle} ref={referenceRef}>
                      <MultiLingualText size="400" color="black" family="openSansCondensed">
                        {item.label}
                      </MultiLingualText>
                      <Box ml={{ sm: '200' }}>
                        <Icon icon="chevron-right" iconStyle="solid" size={IconSize.XSMALL} />
                      </Box>
                    </AccountItemStyled>
                    <div
                      ref={popperRef}
                      data-placement={placement}
                      style={{ position: 'absolute', right: '100%', top: '0px' }}
                    >
                      {isOpen && (
                        <MenuOptions>
                          {map(allThemes, (item) => (
                            <ThemeItemStyled
                              key={item}
                              onClick={() => {
                                setCurrentTheme(ThemeName[item]);
                                setThemeSettings({ ...themeSettings, currentTheme: ThemeName[item] });
                                close();
                              }}
                            >
                              <Box alignItems={{ sm: 'center' }}>
                                <ThemeColorBadge currentTheme={ThemeName[item]} />
                                <MultiLingualText size="400" color="black" family="openSansCondensed" textKey={item} />
                              </Box>
                              {currentTheme === ThemeName[item] && <Icon icon="check" size={IconSize.XSMALL} />}
                            </ThemeItemStyled>
                          ))}
                        </MenuOptions>
                      )}
                    </div>
                  </>
                )}
              </Menu>
            ) : (
              <AccountItemStyled onClick={() => onMenuClick(item.id)}>
                <MultiLingualText size="400" color="black" family="openSansCondensed">
                  {item.label}
                </MultiLingualText>
              </AccountItemStyled>
            )}
          </Fragment>
        );
      })}
    </AccountSectionStyled>
  );
});

export const AppHeader = () => {
  const elementRef = useRef(null);
  const [showToolTipMessage, setShowToolTipMessage] = useState(false);
  const { userAccount } = useAppUser();
  const { open, visible, close } = useSideSheetState(false);
  const { currentTheme } = useAppThemeContext();
  const location = useLocation();
  const { pathname } = location;
  return (
    <Box css={{ position: 'relative' }} flexGrow={{ sm: 1 }}>
      <HeaderBackdropStyled currentTheme={currentTheme} />
      <AppHeaderStyled>
        <LogoSectionStyled>
          <SetRouteLogo></SetRouteLogo>
        </LogoSectionStyled>
        <Box>
          <Divider></Divider>
          <TopMenu />
          {/* Temporary fix until dashboard is developed */}
          {pathname && pathname === '/' && <Navigate to="/commodities/cable" replace={true} />}
        </Box>
        <AppHeaderRightContainerStyled>
          <ProjectSelector />
          <HelpIconStyled ref={elementRef}>
            <Icon
              onMouseOver={() => setShowToolTipMessage(true)}
              onMouseLeave={() => setShowToolTipMessage(false)}
              icon="circle-question"
              iconStyle="solid"
              fillColor="primary.700"
              size={IconSize.MEDIUM}
            />
          </HelpIconStyled>
          <Popover
            triggerComponentRef={elementRef}
            placement="top"
            showPopper={showToolTipMessage}
            customStyle={{ backgroundColor: 'black' }}
          >
            <ToolTipTextStyled>
              <MultiLingualText color="white" size="300">
                Help
              </MultiLingualText>
            </ToolTipTextStyled>
          </Popover>
          <Menu>
            {({ placement, isOpen, toggle, referenceRef, popperRef }) => (
              <>
                <div ref={referenceRef}>
                  <Box onClick={toggle} css={{ cursor: 'pointer' }}>
                    {userAccount?.userAccountPhoto?.photo ? (
                      <Avatar size={AvatarSize.MEDIUM} src={userAccount?.userAccountPhoto?.photo} />
                    ) : (
                      <AvatarPlaceholder
                        nameFirst={userAccount?.nameFirst}
                        nameLast={userAccount?.nameLast}
                        size={AvatarSize.SMALL}
                      />
                    )}
                  </Box>
                </div>
                <div ref={popperRef} data-placement={placement}>
                  {isOpen && (
                    <TopMenuAccountSection
                      onMenuClick={(id) => {
                        if (id === 'MY_ACCOUNT') {
                          open();
                        }
                        toggle();
                      }}
                      toggle={toggle}
                    />
                  )}
                </div>
              </>
            )}
          </Menu>
        </AppHeaderRightContainerStyled>
      </AppHeaderStyled>
      {visible && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader />}>
            <MyAccount close={close} />
          </Suspense>
        </ErrorBoundary>
      )}
    </Box>
  );
};
